import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { Brand, Company, Range, ListBrandsRequest, ListProductsRequestOrderField, Product2, ProductSpecification, ProductType, ProductWorkflow, ProductWorkflowState, QualityLevel, SituViewWebApiClient, WorkflowStateType, ListRangesRequest, ProductClassification, BrandFilterOption, NewListProductsRequest, GetListProductsFiltersRequest, NewListProductsResponse, GetListProductsFiltersResponse, CompanyFilterOption, RangeFilterOption, ProductTypeFilterOption, ProductClassificationFilterOption, ProductStateFilterOption } from 'situview-webapi-client';
import { ProductSelectorDialogComponent, ProductSpecificationTreeDialogComponent, ProductSpecificationTreeResultData, WorkflowTriggerDialogComponent, WorkflowTriggerResultData } from 'src/app/components';
import { AuthService, BreadcrumbService, CompanyViewService, ProductService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateBrandDialogComponent } from '../../products/create-brand-dialog/create-brand-dialog/create-brand-dialog.component';
import { Sort } from '@angular/material/sort';
import { Guid } from 'guid-typescript';


@Component({
  selector: 'product-details-report',
  templateUrl: './product-details-report.component.html',
  styleUrls: ['./product-details-report.component.less'],
})
export class ProductDetailsReportComponent extends SituBaseComponent {

  private readonly defaultColumns = ['select', 'company', 'name', 'sku', 'productType', 'productSpecification', 'description', 'brand', 'qualityLevel', 'state', 'notes', 'calloutsEnabled', 'hideSku', 'enabled', 'edit'];
  public readonly Number = Number;
  public readonly QualityLevel = QualityLevel;
  
  sortBy = ListProductsRequestOrderField.Unset;
  sortDescending = false;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  pageNumber = 1;
  pageSize = 100;
  totalItems = 0;

  products: ProductLineItem[] = [];
  allProductSpecifications: ProductSpecification[] = [];

  displayedColumns: string[] = this.defaultColumns;

  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  companyIdFilter: string | undefined;
  companyNameFilter: string | undefined;
  brandIdFilter: string | undefined;
  brandNameFilter: string | undefined;
  productRangeIdFilter: string | undefined;
  productRangeNameFilter: string | undefined;
  productClassificationIdFilter: string | undefined;
  productClassificationNameFilter: string | undefined;
  productTypeIdFilter: string | undefined;
  productTypeNameFilter: string | undefined;
  productStateFilter: ProductWorkflowState | undefined;
  productStateNameFilter: string | undefined;
  searchTerm: string = "";

  loadingFilterOption = false;

  workflowStateType = WorkflowStateType;

  bulkProductTypeIdFilter: string | undefined;
  bulkProductTypeNameFilter: string | undefined;
  bulkCalloutsEnabled: boolean | undefined;
  bulkHideProductCode: boolean | undefined;
  bulkProductEnabled: boolean | undefined;
    
  workflowTriggerDialogRef: MatDialogRef<WorkflowTriggerDialogComponent> | undefined;
  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;
  productSpecificationTreeDialogRef: MatDialogRef<ProductSpecificationTreeDialogComponent> | undefined;

  dirtyFilterData = false;

  @ViewChild('paginator') paginator: MatPaginator| undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private productService: ProductService,
    private situViewClient: SituViewWebApiClient,
    private companyViewService: CompanyViewService,
    private breadcrumbService: BreadcrumbService,
    private router: Router) {
      super(authService, stateService);

  }
  
  protected onSelectedCompanyChange(): void {
    this.loadCompanies();
    this.loadBrands();
    this.loadProductStates();
    this.loadProductTypes();
    this.loadProductSpecifications();
  }

  ngOnInit() {
    super.ngOnInit();

    this.filteredCompanies = this.companyControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterCompanies(value);
      })
    );

    this.filteredBrands = this.brandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBrands(value);
      })
    );

    this.filteredProductRanges = this.productRangeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        if (typeof value === 'string') {
          return this.filterProductRanges(value);
        } else {
          return this.filterProductRanges();
        }
      })
    );

    this.filteredProductClassifications = this.productClassificationControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductClassifications(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductStates(value);
      })
    );

    this.filteredBulkProductTypes = this.bulkProductTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBulkProductTypes(value);
      })
    );

    this.filteredBulkBrands = this.bulkBrandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBulkBrands(value);
      })
    );
  }

  onSearch() {
    this.paginator?.firstPage();

    this.loadData();
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;

    this.loadData();
  }

  async loadData() {
    var productPage = await this.getProducts();

    if (productPage) {
      this.products = productPage.items!.map(x => new ProductLineItem(x));
      this.totalItems = productPage.totalItems!;
    }
  }

  async getProducts(loadAllProducts = false) : Promise<NewListProductsResponse> {
    this.loading = true;
    
    try{
      var request = this.buildListRequest(loadAllProducts);

      var productPage = await this.situViewClient.newListProducts(request);

      return productPage;
    } catch(error) {
      console.log(error);
    } finally {
      this.loading = false;
    }

    return new NewListProductsResponse();
  }

  async downloadCsv() {
    var dataRows: any[][] = [['ID', 'Company', 'Name', 'Product Code', 'Product Type', 'Product Specification', 'Description', 'Brand', 'Quality Level', 'Current Workflow State', 'Notes', 'Callouts Enabled', 'Hide Sku', 'Enabled']];

    var allProducts = await this.getProducts(true);

    if (allProducts) {
      allProducts.items?.forEach((x: Product2) => {
        const quality = x.qualityLevel
          ? QualityLevel[x.qualityLevel]
          : (x.brand?.qualityLevel ? '*' + QualityLevel[x.brand.qualityLevel] : null);
        dataRows.push([
          x.id ?? '',
          this.companyDirectory[x.id ?? ''] ?? '',
          x.name ?? '',
          x.sku ?? '',
          this.getProductTypeName(x) ?? '',
          this.getProductSpecificationString(x) ?? '',
          x.description,
          x.brand?.id ?? '',
          quality ?? '',
          this.getWorkflowState(x) ?? '',
          x.notes,
          x.calloutsEnabled ? 'TRUE' : 'FALSE',
          x.hideSku ? 'TRUE' : 'FALSE',
          !x.disabled ? 'TRUE' : 'FALSE',
        ]);
      });

      Utils.exportToCsv("product-details.csv", dataRows);
    }
  }

  async loadFilterOptions(request: GetListProductsFiltersRequest): Promise<GetListProductsFiltersResponse> {
    this.loadingFilterOption = true;

    try {
      var response = await this.situViewClient.getListProductsFilters(request);

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.loadingFilterOption = false;
    }

    return new GetListProductsFiltersResponse();
  }

  buildListRequest(loadAllProducts = false) : NewListProductsRequest {
    if (loadAllProducts) {
      var pageNumber = 1;
      var pageSize = 10000;
    } else {
      var pageNumber = this.pageNumber;
      var pageSize = this.pageSize;
    }

    var request = new NewListProductsRequest();
    request.companyId = this.selectedCompany?.id!;

    request.filterCompanyId = this.companyIdFilter;
    request.filterBrandId = this.brandIdFilter;
    request.filterProductClassificationId = this.productClassificationIdFilter;
    request.filterProductTypeId = this.productTypeIdFilter;
    request.filterProductState = this.productStateFilter;
    request.filterRangeId = this.productRangeIdFilter;
    request.searchTerm = this.searchTerm;

    request.orderBy = this.sortBy;
    request.orderDescending = this.sortDescending;
    request.pageNumber = pageNumber;
    request.pageSize = pageSize;

    request.includeProductSpecifications = true;
    request.includeCurrentState = true;
    request.includeBrand = true;

    return request;
  }
  
  buildFilterOptionsRequest() : GetListProductsFiltersRequest {
    var request = this.buildListRequest();
    return GetListProductsFiltersRequest.fromJS(request);
  }

  canEditProductSpecifications(item: ProductLineItem) {
    var product = item.product;

    var hasProductSpecificationsToSelect = this.allProductSpecifications.some(x => x.productTypeId === product.productTypeId);
    return hasProductSpecificationsToSelect;
  }
  
  canBulkEditProductSpecifications() {
    var editModeProducts = this.products.filter(e => e.editMode);

    if (editModeProducts.length === 0) {
      return false;
    } else if (editModeProducts.length > 1) {
      var firstProduct = editModeProducts[0].product;
      var haveSameProductType = editModeProducts.every(e => e.product.productTypeId === firstProduct.productTypeId);

      if (!haveSameProductType) {
        return false;
      }
    }

    return this.canEditProductSpecifications(editModeProducts[0]);
  }

  onBulkOpenProductSpecificationDialog() {

    var editModeProducts = this.products.filter(e => e.editMode);
    var firstProduct = editModeProducts[0].product;
    var productSpecificationIds = firstProduct.productSpecificationIds!;

    var processResult = async (result: ProductSpecificationTreeResultData) => {
      for (var lineItem of editModeProducts) {
        lineItem.product.productSpecificationIds = [...result.selectedProductSpecificationIds];
      }
    }

    this.openProductSpecificationDialog(productSpecificationIds, firstProduct.productTypeId!, processResult);

  }

  onOpenProductSpecificationDialog(item: ProductLineItem) {

    var product = item.product;
    var processResult = async (result: ProductSpecificationTreeResultData) => {
      product.productSpecificationIds = [...result.selectedProductSpecificationIds];
    }

    this.openProductSpecificationDialog(product.productSpecificationIds!, product.productTypeId!, processResult);
  }

  openProductSpecificationDialog(productSpecificationIds: string[], productTypeIdFilter: string,
    processResult: (result: ProductSpecificationTreeResultData) => void) {
    const dialogWidth = '800px';
    const dialogHeight = '700px';

    this.productSpecificationTreeDialogRef = this.dialog.open(ProductSpecificationTreeDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        productSpecificationIds: productSpecificationIds,
        productTypeIdFilter: productTypeIdFilter,
        productSpecifications: this.allProductSpecifications,
      }
    });

    this._subscriptions.push(this.productSpecificationTreeDialogRef.afterClosed()
      .subscribe((data: ProductSpecificationTreeResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }

  sortData(sort: Sort) {

    console.log(sort);

    if (!sort.active || sort.direction === '') {
      this.sortBy = ListProductsRequestOrderField.Unset;
      this.loadData();
      return;
    }

    this.sortDescending = sort.direction === 'desc';

    switch (sort.active) {
      case 'name':
        this.sortBy = ListProductsRequestOrderField.Name;
        break;
      case 'company':
        this.sortBy = ListProductsRequestOrderField.Company;
        break;
      case 'sku':
        this.sortBy = ListProductsRequestOrderField.ProductCode;
        break;
      case 'productType':
        this.sortBy = ListProductsRequestOrderField.ProductType;
        break;
      case 'brand':
        this.sortBy = ListProductsRequestOrderField.Brand;
        break;
      case 'qualityLevel':
        this.sortBy = ListProductsRequestOrderField.QualityLevel;
        break;
      case 'state':
        this.sortBy = ListProductsRequestOrderField.CurrentWorkflowState;
        break;
      case 'calloutsEnabled':
        this.sortBy = ListProductsRequestOrderField.CalloutsEnabled;
        break;
      case 'hideSku':
        this.sortBy = ListProductsRequestOrderField.HideProductCode;
        break;
      case 'enabled':
        this.sortBy = ListProductsRequestOrderField.ProductEnabled;
        break;
      default:
        this.sortBy = ListProductsRequestOrderField.Unset;
        break;
    }
    
    this.loadData();
  }

  getCompanyName(item: Product2) : string | undefined {
    if (item.companyId) {
      return this.companyDirectory[item.companyId];
    }
  }

  getProductTypeName(item: Product2) : string | undefined {
    var productType = this.allProductTypes.find(x => x.id === item.productTypeId)
    return productType?.name;
  }

  getProductSpecificationString(item: Product2) : string | undefined {
    if (!item.productSpecificationIds || !this.allProductSpecifications) {
      return undefined;
    }

    var specificationNames = this.allProductSpecifications
      .filter(x => item.productSpecificationIds?.includes(x.id!))
      .map(x => x.name);

    var specificationString = specificationNames.join(" ");
    return specificationString;
  }

  editAllLineItems(enable: boolean) {
    this.products.forEach(x => {
      if (x.editMode !== enable) {
        this.editLineItem(x, enable);
      }
    });
  }

  editLineItem(item: ProductLineItem, enable: boolean) {
    if (enable) {
      this.enableEditMode(item);
    } else {
      this.cancelEditMode(item);
    }
  }

  enableEditMode(item: ProductLineItem) {
    item.editMode = true;
    item.originalProduct = new Product2(item.product);

    item.brandControl.setValue(this.brandDirectory[item.product?.brandId ?? '']);

    item.filteredProductTypes = item.productTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    item.filteredBrands = item.brandControl.valueChanges.pipe(
      startWith(item.brandControl.value),
      map((input) => this.filterBulkBrands(input, item.product.companyId)) 
    )
  
    // TODO see if there is a better way to do this without the timeout
    new Promise(resolve => setTimeout(resolve, 100)) // Hack, we need to wait for 'valueChanges.pipe' to be bound, it is not immediate 
    .then(() => {
      this.products.forEach(item => {
        item.productTypeNameFilter = this.allProductTypes?.find(x => x.id === item.product.productTypeId)?.name;
        item.productTypeControl.setValue(item.productTypeNameFilter);
        item.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
    });

    this.bulkCalloutsEnabled = undefined;
    this.bulkHideProductCode = undefined;
    this.bulkProductEnabled = undefined;
  }

  cancelEditMode(item: ProductLineItem) {
    item.editMode = false;
    if (item.originalProduct) {
      item.product = item.originalProduct;
      item.originalProduct = undefined;
    }
  }

  onLineItemProductTypeSelected(event: any, lineItem: ProductLineItem): void {
    var productTypeName = event.option.value;
    lineItem.product.productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    lineItem.productTypeNameFilter = productTypeName;
  }

  onLineItemBrandSelected(event: any, lineItem: ProductLineItem): void {
    var brandId = event.option.value;
    lineItem.product.brandId = brandId;
  }

  hasEditModeLineItems(): boolean {
    if (!this.products) {
      return false;
    }

    return this.products.some(x => x.editMode);
  }

  countEditModeLineItems(): number {
    var count = 0;
    if (this.products) {
      
      for (var product of this.products) {
        if (product.editMode) {
          count++;
        }
      }
    }
    
    return count;
  }

  editSameProductType(productTypeId: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.productTypeId === productTypeId) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductSpecification(productSpecificationIds: string[] | undefined, enable: boolean) {
    this.products.forEach(x => {
      var hasSameSpecifications = productSpecificationIds &&
        x.product.productSpecificationIds &&
        productSpecificationIds.length === x.product.productSpecificationIds.length &&
        productSpecificationIds.every((value, index) => value === x.product.productSpecificationIds![index]);

      if (hasSameSpecifications) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameDescription(description: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.description === description) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameBrand(brandId: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.brandId === brandId) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameQualityLevel(qualityLevel: QualityLevel, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.qualityLevel === qualityLevel) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductWorkflowState(productState: ProductWorkflowState, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.currentWorkflowState!.state === productState) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameNotes(notes: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.notes === notes) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameCalloutsEnabled(calloutsEnabled: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.calloutsEnabled === calloutsEnabled) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameHideSku(hideSku: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.hideSku === hideSku) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductDisabled(disabled: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.disabled === disabled) {
        this.editLineItem(x, enable)
      }
    });
  }
  
  private loadProductSpecifications() {
    return this.productService.LoadProductSpecifications()
      .then((ProductSpecifications: ProductSpecification[]) => {
        this.allProductSpecifications = ProductSpecifications;
      });
  }

  onSaveLineItem(item: ProductLineItem) {
    this.saveLineItem(item);
  }
  
  async onSaveAllEditModeLineItems() {
    this.loading = true;

    var updateCount = 0;
    var failCount = 0;

    try {
      for (let productLineItem of this.products) {
        if (!productLineItem.editMode) {
          continue;
        }

        var updatedProduct = await this.productService.UpdateProduct(productLineItem.product);

        if (updatedProduct) {
          this.updateExistingLineItem(updatedProduct);
          updateCount++;
        } else {
          failCount++;
          this.showMessage("There was an issue updating the products.");
        }
      }
    } finally {
      this.loading = false;
    }

    
    this.showMessage(`${updateCount} Products have been updated with ${failCount} errors.`);
  }
  
  saveLineItem(item: ProductLineItem) {
    this.loading = true;

    this.productService.UpdateProduct(item.product)
    .then((product: Product2 | undefined) => {
      if (product) {
        this.updateExistingLineItem(product);
        this.showMessage("The Product has been updated");
      } else {
        this.showMessage("There was an issue updating the product.");
      }
    })
    .finally(() => {
      this.loading = false;
    });

  }
  
  updateExistingLineItems(updatedProducts: Product2[]) {
    updatedProducts.forEach(x => this.updateExistingLineItem(x));
  }

  updateExistingLineItem(updatedProduct: Product2) {
    var existingLineItem = this.products.find(y => y.product.id === updatedProduct.id);
    if (existingLineItem) {
      existingLineItem.product = updatedProduct;
      existingLineItem.editMode = false;
    } else {
      this.products.push(new ProductLineItem(updatedProduct));
    }
  }

  searchTermChanged() {
    this.clearAllFilterOptions();
  }

  clearSearchTerm() {
    this.searchTerm = '';
    this.clearAllFilterOptions();
  }

  clearAllFilterOptions(optionsToKeep?: any[]) {
    if (optionsToKeep != this.companyFilterOptions) {
      this.companyFilterOptions = undefined;
    } 
    if (optionsToKeep != this.brandFilterOptions) {
      this.brandFilterOptions = undefined;
    }
    if (optionsToKeep != this.productRangeFilterOptions) {
      this.productRangeFilterOptions = undefined;
    }
    if (optionsToKeep != this.productClassificationFilterOptions) {
      this.productClassificationFilterOptions = undefined;
    }
    if (optionsToKeep != this.productTypeFilterOptions) {
      this.productTypeFilterOptions = undefined;
    }
    if (optionsToKeep != this.productStateFilterOptions) {
      this.productStateFilterOptions = undefined;
    }
  }
  
  //#region Company AutoComplete Filter

  companyControl = new FormControl();
  filteredCompanies: Observable<CompanyFilterOption[]> | undefined;
  companyFilterOptions?: CompanyFilterOption[];

  async loadFilterCompanyOptions() {
    if (this.loadingFilterOption || this.companyFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterCompanyOptions = true;
    var response = await this.loadFilterOptions(request);

    this.companyFilterOptions = response.filterCompanyOptions;
    this.companyNameFilter = this.companyFilterOptions?.find(x => x.id === this.companyIdFilter)?.name;
    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onCompanySelected(event: any): void {
    this.selectCompanyByName(event.option.value);

    this.clearAllFilterOptions(this.companyFilterOptions);
  }

  selectCompanyByName(companyName: string) {
    this.companyIdFilter = this.companyFilterOptions?.find(x => x.name === companyName)?.id;
    this.companyNameFilter = companyName;
  }

  selectCompanyById(companyId: string) {
    this.companyIdFilter = companyId;
    this.companyNameFilter = this.companyFilterOptions?.find(x => x.id === companyId)?.name;
  }

  onClearCompany() {
    this.clearCompany();
  }

  clearCompany() {
    if (this.companyNameFilter) {
      this.clearAllFilterOptions();
    }

    this.companyControl.reset();
    this.companyIdFilter = undefined;
    this.companyNameFilter = undefined;
  }

  private filterCompanies(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.companyFilterOptions;

    if (!companies) {
      return [];
    }

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }

  //#endregion Company AutoComplete Filter
    
  //#region Brand AutoComplete Filter

  brandControl = new FormControl();
  filteredBrands: Observable<BrandFilterOption[]> | undefined;
  brandFilterOptions?: BrandFilterOption[];

  async loadFilterBrandOptions() {
    if (this.loadingFilterOption || this.brandFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterBrandOptions = true;
    var response = await this.loadFilterOptions(request);

    this.brandFilterOptions = response.filterBrandOptions;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === this.brandIdFilter)?.name;
    this.brandControl.setValue(this.brandIdFilter);
    this.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBrandSelected(event: any): void {
    this.selectBrandById(event.option.value);

    this.clearAllFilterOptions(this.brandFilterOptions);
  }

  displayBrandFilterName = (brandId: string): string => {
    if (!brandId) return '';
    return this.brandFilterOptions?.find(x => x.id === brandId)?.name ?? brandId;
  }

  selectBrandByName(brandName: string) {
    this.brandIdFilter = this.brandFilterOptions?.find(x => x.name === brandName)?.id;
    this.brandNameFilter = brandName;
  }

  selectBrandById(brandId: string) {
    this.brandIdFilter = brandId;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === brandId)?.name;
  }

  onClearBrand() {
    this.clearBrand();
  }

  clearBrand() {
    if (this.brandNameFilter) {
      this.clearAllFilterOptions();
    }

    this.brandControl.reset();
    this.brandIdFilter = undefined;
    this.brandNameFilter = undefined;
  }

  private filterBrands(searchTerm: string | undefined = undefined): Brand[] {
    var brands = this.brandFilterOptions;

    if (!brands) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  //#endregion Brand AutoComplete Filter

  //#region Shared Range AutoComplete Filter

  productRangeControl = new FormControl();
  filteredProductRanges: Observable<RangeFilterOption[]> | undefined;
  productRangeFilterOptions?: RangeFilterOption[];

  async loadFilterProductRangeOptions() {
    if (this.loadingFilterOption || this.productRangeFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterRangeOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productRangeFilterOptions = response.filterRangeOptions;
    this.productRangeNameFilter = this.productRangeFilterOptions?.find(x => x.id === this.productRangeIdFilter)?.name;
    this.productRangeControl.setValue(this.productRangeNameFilter);
    this.productRangeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductRangeSelected(event: any): void {
    this.selectProductRange(event.option.value);

    this.clearAllFilterOptions(this.productRangeFilterOptions);
  }

  selectProductRange(range: RangeFilterOption) {
    this.productRangeIdFilter = range?.id;
    this.productRangeNameFilter = range?.name;
  }

  displayRange(range: Range): string {
    return range?.name ?? '';
  }

  selectProductRangeById(productRangeId: string) {
    this.productRangeIdFilter = productRangeId;
    this.productRangeNameFilter = this.productRangeFilterOptions?.find(x => x.id === productRangeId)?.name;
  }

  onClearProductRange() {
    this.clearProductRange();
  }

  clearProductRange() {
    if (this.productRangeNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productRangeControl.reset();
    this.productRangeIdFilter = undefined;
    this.productRangeNameFilter = undefined;
  }

  private filterProductRanges(searchTerm: string | undefined = undefined): RangeFilterOption[] {
    var productRanges = this.productRangeFilterOptions;

    if (!productRanges) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      var searchTermLower = searchTerm.toLowerCase();
      productRanges = productRanges.filter(range => range.name?.toLowerCase().includes(searchTermLower));
    }

    return productRanges;
  }

  //#endregion Shared Range AutoComplete Filter
  
  //#region Product Classification AutoComplete Filter

  productClassificationControl = new FormControl();
  filteredProductClassifications: Observable<ProductClassificationFilterOption[]> | undefined;
  productClassificationFilterOptions?: ProductClassificationFilterOption[];

  async loadFilterProductClassificationOptions() {
    if (this.loadingFilterOption || this.productClassificationFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductClassificationOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productClassificationFilterOptions = response.filterProductClassificationOptions;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === this.productClassificationIdFilter)?.name;
    this.productClassificationControl.setValue(this.productClassificationNameFilter);
    this.productClassificationControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }
  
  onProductClassificationSelected(event: any): void {
    this.selectProductClassificationByName(event.option.value);

    this.clearAllFilterOptions(this.productClassificationFilterOptions);
  }

  selectProductClassificationByName(productClassificationName: string) {
    this.productClassificationIdFilter = this.productClassificationFilterOptions?.find(x => x.name === productClassificationName)?.id;
    this.productClassificationNameFilter = productClassificationName;
  }

  selectProductClassificationById(productClassificationId: string) {
    this.productClassificationIdFilter = productClassificationId;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === productClassificationId)?.name;
  }

  onClearProductClassification() {
    this.clearProductClassification();
  }

  clearProductClassification() {
    if (this.productClassificationNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productClassificationControl.reset();
    this.productClassificationIdFilter = undefined;
    this.productClassificationNameFilter = undefined;
  }

  private filterProductClassifications(searchTerm: string | undefined = undefined): ProductClassificationFilterOption[] {
    var productClassifications = this.productClassificationFilterOptions;

    if (!productClassifications) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productClassifications = productClassifications.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productClassifications;
  }

  //#endregion Product Classification AutoComplete Filter
  
  //#region Product Type AutoComplete Filter

  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductTypeFilterOption[]> | undefined;
  productTypeFilterOptions?: ProductTypeFilterOption[];

  async loadFilterProductTypeOptions() {
    if (this.loadingFilterOption || this.productTypeFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductTypeOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productTypeFilterOptions = response.filterProductTypeOptions;
    this.productTypeNameFilter = this.productTypeFilterOptions?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);

    this.clearAllFilterOptions(this.productTypeFilterOptions);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeIdFilter = this.productTypeFilterOptions?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeIdFilter = productTypeId;
    this.productTypeNameFilter = this.productTypeFilterOptions?.find(x => x.id === productTypeId)?.name;
  }

  onClearProductType() {
    this.clearProductType();
  }

  clearProductType() {
    if (this.productTypeNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productTypeControl.reset();
    this.productTypeIdFilter = undefined;
    this.productTypeNameFilter = undefined;
  }

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductTypeFilterOption[] {
    var productTypes = this.productTypeFilterOptions;

    if (!productTypes) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }

  //#endregion Product Type AutoComplete Filter

  //#region Product State AutoComplete Filter

  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductStateFilterOption[]> | undefined;
  productStateFilterOptions?: ProductStateFilterOption[];

  async loadFilterProductStateOptions() {
    if (this.loadingFilterOption || this.productStateFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductStateOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productStateFilterOptions = response.filterProductStateOptions;
    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === this.productStateFilter)?.name;
    this.productStateControl.setValue(this.productStateNameFilter);
    this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);

    this.clearAllFilterOptions(this.productStateFilterOptions);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === productState)?.name;
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.productStateFilterOptions?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
  }

  onClearProductState() {
    this.clearProductState();
  }

  clearProductState() {
    if (this.productStateNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductStateFilterOption[] {
    var productStates = this.productStateFilterOptions;

    if (!productStates) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  //#endregion Product State AutoComplete Filter

  //#region Bulk Company AutoComplete Filter

  allCompanies: Company[] = [];
  companyDirectory: Record<string, string> = {};

  private async loadCompanies() {
    this.allCompanies = await this.companyViewService.LoadCompanies();

    this.allCompanies.forEach(c => this.companyDirectory[c.id!] = c.name!);
    
    this.companyNameFilter = this.allCompanies?.find(x => x.id === this.companyIdFilter)?.name;
    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  private filterBulkCompanies(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }

  //#endregion Bulk Company AutoComplete Filter

  //#region Bulk Brand AutoComplete Filter

  bulkBrandControl = new FormControl();
  filteredBulkBrands: Observable<Brand[]> | undefined;
  brandDirectory: Record<string, string> = {};
  allBrands: Brand[] = [];

  displayBrandName = (brandId: string): string => {
    if (!brandId) return '';
    return this.allBrands?.find(x => x.id === brandId)?.name ?? brandId;
  }

  private async loadBrands() {
    var response = await this.situViewClient.listBrands(new ListBrandsRequest());

    this.allBrands = response.brands!;
    
    this.brandDirectory = {};
    this.allBrands.forEach(b => this.brandDirectory[b.id!] = b.name!);

    this.brandControl.setValue(this.brandNameFilter);
    this.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBulkBrandSelected(event: any) {
    var brandId = event.option.value;

    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.brandId = brandId;
        x.brandControl.setValue(brandId);
      }
    });
  }

  clearBulkBrand() {
    this.bulkBrandControl.reset();
  }

  onBulkQualityLevelChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        let value = event.target.value
        x.product.qualityLevel = value ? Number(event.target.value) : undefined;
      }
    });
  }

  private filterBulkBrands(searchTerm: string | undefined = undefined, supplierId?: string): Brand[] {
    var brands = this.allBrands;

    if (supplierId) {
      brands = brands.filter(brand => brand.companyId === supplierId);
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  //#endregion Bulk Brand AutoComplete Filter

  //#region Bulk Product Type AutoComplete Filter

  bulkProductTypeControl = new FormControl();
  filteredBulkProductTypes: Observable<ProductType[]> | undefined;
  allProductTypes: ProductType[] = [];
  
  private async loadProductTypes() {
    this.allProductTypes = await this.productService.LoadProductTypes();
    
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBulkProductTypeSelected(event: any) {
    var productTypeName = event.option.value;
    var productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;

    return this.products.forEach(x => {
      if (x.editMode) {
      x.productTypeNameFilter = productTypeName;
      x.product.productTypeId = productTypeId;
      x.productTypeControl.setValue(x.productTypeNameFilter);
      x.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkProductType() {
    this.bulkProductTypeControl.reset();
    this.bulkProductTypeIdFilter = undefined;
    this.bulkProductTypeNameFilter = undefined;
  }

  private filterBulkProductTypes(searchTerm: string | undefined = undefined): ProductType[] {
    var productTypes = this.allProductTypes;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }

  //#endregion Bulk Product Type AutoComplete Filter

  //#region Bulk Workflow
  
  allProductStates: ProductWorkflow[] = [];
  
  private async loadProductStates() {
    var productWorkflow = await this.productService.LoadProductWorkflowConfiguration();

    this.allProductStates = productWorkflow;
    
    this.productStateNameFilter = this.allProductStates?.find(x => x.state === this.productStateFilter)?.name;
    this.productStateControl.setValue(this.productStateNameFilter);
    this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  getWorkflowState(item: Product2) : string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  onBulkOpenWorkflowDialog() {

    var processResult = async (result: WorkflowTriggerResultData) => {

      var count = 0;
      var errorCount = 0;

      for (var lineItem of this.products) {

        if (lineItem.editMode)
        {
          var product = lineItem.product;
          var triggerResult = await this.productService.TriggerProductWorkflow(product.companyId!, product.id!, result.selectedTransition?.trigger, result.comments);

          count ++;
          if (triggerResult) {
            product.currentWorkflowState = triggerResult;
            if (lineItem.originalProduct) {
              lineItem.originalProduct.currentWorkflowState = triggerResult;
            }
          } else {
            errorCount++;
          }

          this.showMessage(`Workflow transitioned ${count} products with ${errorCount} errors!`);
        }
      }
    }

    // It should not be possible to get here unless all the workflow states are the same
    var workflowState = this.workflowStateOfProductsInEditModeAreTheSame();
    this.openWorkflowDialog(workflowState!, undefined, processResult);

  }

  onOpenWorkflowDialog(item: ProductLineItem) {

    var product = item.product;
    var processResult = async (result: WorkflowTriggerResultData) => {
      var triggerResult = await this.productService.TriggerProductWorkflow(product.companyId!, product.id!, result.selectedTransition?.trigger, result.comments);
      
      if (triggerResult) {
        var stateHasChanged = product.currentWorkflowState?.state !== triggerResult?.state;
        product.currentWorkflowState = triggerResult;
        if (item.originalProduct) {
          item.originalProduct.currentWorkflowState = triggerResult;
        }

        if (stateHasChanged) {
          this.showMessage(`Workflow successfully transitioned to ${triggerResult.name}!`);
        } else {
          this.showMessage(`Comment successfully added!`);
        }
      } else {
        this.showMessage("Failed to update workflow!");
      }
        
    }

    this.openWorkflowDialog(product.currentWorkflowState!, product, processResult);
  }

  openWorkflowDialog(workflowState: ProductWorkflow, workflowSubject: any | undefined,
    processResult: (result: WorkflowTriggerResultData) => void) {
    const dialogWidth = '800px';
    const dialogHeight = '500px';

    this.workflowTriggerDialogRef = this.dialog.open(WorkflowTriggerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        workflowSubject: workflowSubject,
        workflowState: workflowState,
      }
    });

    this._subscriptions.push(this.workflowTriggerDialogRef.afterClosed()
      .subscribe((data: WorkflowTriggerResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }
  
  workflowStateOfProductsInEditModeAreTheSame(): ProductWorkflow | undefined {
    // Filter the objects to find the selected ones
    const editModeProducts = this.products.filter(x => x.editMode);

    // Check if there are no selected objects
    if (editModeProducts.length === 0) {
        return undefined;
    }

    var firstWorkflowState = editModeProducts[0].product.currentWorkflowState;
    // Check if all workflow states are the same
    const allSame = editModeProducts.every(x => x.product.currentWorkflowState!.state === firstWorkflowState!.state);

    return allSame ? firstWorkflowState : undefined;
  }

  //#endregion Bulk Workflow

  //#region Bulk Changed Event

  onBulkDescriptionChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.description = event.target.value;
      }
    });
  }
    
  onBulkBrandChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.brand = event.target.value;
      }
    });
  }

  onBulkNotesChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.notes = event.target.value;
      }
    });
  }

  onBulkCalloutsEnabledChanged(value: boolean) {
    this.bulkCalloutsEnabled = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.calloutsEnabled = value;
      }
    });
  }

  onBulkHideProductCodeChanged(value: boolean) {
    this.bulkHideProductCode = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.hideSku = value;
      }
    });
  }

  onBulkProductEnabledChanged(value: boolean) {
    this.bulkProductEnabled = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.disabled = !value;
      }
    });
  }

  //#endregion Bulk Changed Event

  isDefaultId(id: string){
    return id === Guid.EMPTY;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class ProductLineItem {
  product: Product2;
  originalProduct: Product2 | undefined;

  editMode = false;
  
  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  productTypeNameFilter: string | undefined;

  brandControl = new FormControl();
  filteredBrands: Observable<Brand[]> | undefined;

  constructor(product: Product2) {
    this.product = product;
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.brandControl.reset();
    this.product.productTypeId = undefined;
    this.productTypeNameFilter = undefined;
  }

  clearBrand() {
    this.brandControl.reset();
    this.product.brandId = undefined;
  }

}